import { Component, ElementRef, EventEmitter, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Table } from 'primeng/table';
import { BehaviorSubject, Observable, Subject, forkJoin, take, takeUntil } from 'rxjs';
import { AuthService } from 'src/app/auth/auth.service';
import { LoaderService } from 'src/app/services/loader.service';
import { LoginService } from 'src/app/services/login.service';
import { AppUtils } from 'src/app/shared/app.utils';
import { AdminService } from '../../services/admin.service';
import { AppConstants } from '../../shared/app.constants';

@Component({
  selector: 'ness-user-list',
  templateUrl: './user-list.component.html'
})
export class UserListComponent implements OnInit, OnDestroy {
  @Output() onChange = new EventEmitter<{ originalEvent: MouseEvent, index: number }>();
  @ViewChild('dt', { static: false }) userListTable: Table;

  cols: any[] = [
    { field: 'LastName', header: 'Last Name' },
    { field: 'FirstName', header: 'First Name' },
    { field: 'Email', header: 'Email' },
    { field: 'PhoneNumber', header: 'Phone Number' },
    { field: 'AgencyName', header: 'Agency' },
    { field: 'UserTypeDisplay', header: 'User Type' },
    { field: 'ConcatenatedRoles', header: 'User Role' },
    { field: 'DisabledDisplay', header: 'Enabled/Disabled Status' },
    { field: 'RecertificationYear', header: 'Recertification Year' },
    { field: 'LastLoginTimestamp', header: 'Last Login' }
  ];
  rows: number = AppConstants.defaultTableRows;
  exportFilename: string = "UserList";
  canUpdateUsers: boolean = false;
  currentUser: ServerModels.UserInfoViewModel;
  filteredUsers: ServerModels.UserInfoViewModel[] = [];
  showSidebar: boolean = false;
  showUserInfoDetailView: boolean = false;
  loading: boolean = false;
  totalRecords: number = 0;

  private _unsubscribe$: Subject<void> = new Subject<void>();
  private _userHistory: ServerModels.ENESS_UserHistory[] = [];
  userHistorySubj: BehaviorSubject<ServerModels.ENESS_UserHistory[]> = new BehaviorSubject<ServerModels.ENESS_UserHistory[]>(this._userHistory);
  userHistory$: Observable<ServerModels.ENESS_UserHistory[]> = this.userHistorySubj.asObservable();

  private _userInfoForOtherUser: ServerModels.UserInfoViewModel = null;
  userInfoForOtherUserSubj: BehaviorSubject<ServerModels.UserInfoViewModel> = new BehaviorSubject<ServerModels.UserInfoViewModel>(this._userInfoForOtherUser);
  userInfoForOtherUser$: Observable<ServerModels.UserInfoViewModel> = this.userInfoForOtherUserSubj.asObservable();

  constructor(public hostElement: ElementRef,
    public adminService: AdminService,
    private loginService: LoginService,
    private loaderService: LoaderService,
    private authService: AuthService) {
    this.loginService.userInfoData$.pipe(takeUntil(this._unsubscribe$)).subscribe(
      (info: ServerModels.UserInfoViewModel) => this.currentUser = info
    );

    this.adminService.userListTotalRecords$.pipe(takeUntil(this._unsubscribe$)).subscribe((total:number) => this.totalRecords = total);
  }

  ngOnInit() {
    if (this.authService.hasRole(['NESS Administrator'])) {
      let newCols = [...this.cols.slice(0, 4), ...[{ field: 'FieldDivisionName', header: 'Field Division' },
      { field: 'FieldDivisionGroup', header: 'Group' }], ...this.cols.slice(4)];

      this.cols = [...newCols];
    } else {
      this.cols = [
        { field: 'LastName', header: 'Last Name' },
        { field: 'FirstName', header: 'First Name' },
        { field: 'Email', header: 'Email' },
        { field: 'PhoneNumber', header: 'Phone Number' },
        { field: 'AgencyName', header: 'Agency' },
        { field: 'UserTypeDisplay', header: 'User Type' },
        { field: 'ConcatenatedRoles', header: 'User Role' },
        { field: 'DisabledDisplay', header: 'Enabled/Disabled Status' },
        { field: 'RecertificationYear', header: 'Recertification Year' },
        { field: 'LastLoginTimestamp', header: 'Last Login' }
      ];
    }

    this.adminService.getCanUpdateUsers().pipe(takeUntil(this._unsubscribe$)).subscribe(
      (resp:boolean) => this.canUpdateUsers = resp
    );
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  public userUpdated(updated: boolean): void {
    if (updated) {
      this._getUserHistory();
      this.userListTable.onLazyLoad.emit(this.userListTable.createLazyLoadMetadata());
    }
  }

  public viewUserDetails(user: ServerModels.UserInfoViewModel): void {
    this.loading = false;
    this._userInfoForOtherUser = { ...user };

    forkJoin({
      userRoles: this.adminService.getRolesForUserById(this._userInfoForOtherUser.Id),
      history: this.adminService.getUserHistoryByUsername(this._userInfoForOtherUser.Email)
    })
    .pipe(take(1)).subscribe({
      next: (val: { userRoles: ServerModels.RoleViewModel[], history: ServerModels.ENESS_UserHistory[] }) => {
        this._userInfoForOtherUser.Roles = [...val.userRoles];
        this._userHistory = [...val.history];
      },
      complete: () => {
        this.userInfoForOtherUserSubj.next(this._userInfoForOtherUser);
        this.userHistorySubj.next(this._userHistory);
        this.showUserInfoDetailView = true;
        this.showSidebar = true;
      }
    });
  }

  public exportUserList(options?: any): void {
    let lazyLoadEvt: any = this.userListTable.createLazyLoadMetadata();
    this.adminService.getUserListExport({ lazyLoadEvent: JSON.stringify(lazyLoadEvt) }).pipe(takeUntil(this._unsubscribe$)).subscribe({
      next: (userList: ServerModels.UserInfoViewModel[]) => AppUtils.exportCSV(AppUtils.createCSVString({ data: userList, columns: this.cols, selectionOnly: (options && options.selectionOnly), csvSeparator: ',' }), {
        fileName: this.exportFilename
      })
    });
  }

  private _getUserHistory(): void {
    this._userHistory = [];
    this.adminService.getUserHistoryByUsername(this._userInfoForOtherUser.Email).pipe(takeUntil(this._unsubscribe$)).subscribe({
      next: (history: ServerModels.ENESS_UserHistory[]) => {
        this._userHistory = [...history];
      },
      error: (err: any) => this.userHistorySubj.next(this._userHistory),
      complete: () => {
        this.userHistorySubj.next(this._userHistory);
      }
    });
  }

  public onShowSidebar(): void {
    window.setTimeout(() => {
      let targetEl = this.hostElement.nativeElement.getElementsByClassName('ui-sidebar-active');
      if (targetEl.length) {
        targetEl[0].scrollTop = 0;
        targetEl[0].setAttribute("tabindex", "-1");
        targetEl[0].focus();
      }
    }, 1000);
  }

  public onHideSidebar(): void {
    this._userInfoForOtherUser = null;
    this.userInfoForOtherUserSubj.next(null);
    this._userHistory = null;
    this.userHistorySubj.next(null);

    this.showUserInfoDetailView = false;
    this.showSidebar = false;
  }
}
